import { ReactNode, useState } from "react";
import { Activity, ArrowDownLeft, ArrowUpRight, Landmark, Wallet, FileSpreadsheet, Loader2 } from "lucide-react";
import AccountingListTable from "../../components/AccountingList";
import {
  AccountingEntry,
  useGetAllAccountingEntries,
  useDownloadCashReportExcel,
} from "../../service/accountingService";
import { useGetAllBranches } from "../../service/branchService";
import { cn } from "../../utils/cn";

const fmt = (value: number | null) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value ?? 0);

const today = () => new Date().toISOString().slice(0, 10);

function AccountingReport() {
  const [date, setDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState("");

  const { data: branchData } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const {
    data,
    isLoading,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useGetAllAccountingEntries({
    branchId: selectedBranch,
    date,
    pageNo: 0,
    pageSize: 100,
    sortBy: "date",
    sortDir: "desc",
  });

  const entries: AccountingEntry[] = data?.entries ?? [];
  const totalCredit = data?.totalCredit ?? 0;
  const totalDebit = data?.totalDebit ?? 0;
  const expectedCash = data?.expectedCash ?? 0;
  const expectedBank = data?.expectedBank ?? 0;
  const openingBalance = data?.openingBalance ?? 0;
  const receivable = data?.receivable ?? 0;
  const payable = data?.payable ?? 0;

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "-";

  const { mutate: downloadExcel, isPending: isDownloading } = useDownloadCashReportExcel();

  const handleExport = () => {
    if (!selectedBranch || !date) return;
    downloadExcel({
      branchId: Number(selectedBranch),
      date,
    });
  };

  const inputCls =
    "h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
  const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";

  return (
    <div className="space-y-5">
      {/* Header controls */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-5 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Date</label>
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Branch</label>
              <select
                value={selectedBranch}
                onChange={(event) => setSelectedBranch(event.target.value)}
                className={cn(inputCls, "min-w-[220px] pr-8")}
              >
                <option value="">Select branch</option>
                {branchData?.content.map((branch) => (
                  <option
                    key={branch.branchId}
                    value={String(branch.branchId)}
                  >
                    {branch.branchName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-[#8A9BB5]">
              Last updated: {lastUpdated}
            </span>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={!selectedBranch || isFetching}
              className="h-11 rounded-[12px] bg-white border border-[#E8EEF9] px-5 text-[#52627C] text-sm font-medium transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isFetching ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={!selectedBranch || isDownloading}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-sm font-medium text-white transition-colors hover:bg-[#0E4FDE] disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {!selectedBranch ? (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white py-16 text-center shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <Landmark className="mx-auto mb-3 h-10 w-10 text-[#D0DAEE]" />
          <p className="text-sm font-medium text-[#52627C]">
            Select a branch to view real-time accounting data.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Tiles Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <SummaryCard
              label="Total Credit"
              value={fmt(totalCredit)}
              tone="green"
              icon={<ArrowDownLeft className="h-5 w-5" />}
            />
            <SummaryCard
              label="Total Debit"
              value={fmt(totalDebit)}
              tone="red"
              icon={<ArrowUpRight className="h-5 w-5" />}
            />
            <SummaryCard
              label="Receivable"
              value={fmt(receivable)}
              tone="blue"
              icon={<Wallet className="h-5 w-5" />}
            />
            <SummaryCard
              label="Payable"
              value={fmt(payable)}
              tone="red"
              icon={<ArrowUpRight className="h-5 w-5" />}
            />
            <SummaryCard
              label="Expected Cash"
              value={fmt(expectedCash)}
              tone="blue"
              icon={<Activity className="h-5 w-5" />}
            />
            <SummaryCard
              label="Expected Bank"
              value={fmt(expectedBank)}
              tone="blue"
              icon={<Landmark className="h-5 w-5" />}
            />
            <SummaryCard
              label="Opening Balance"
              value={fmt(openingBalance)}
              tone="slate"
              icon={<Activity className="h-5 w-5" />}
            />
          </div>

          {/* Transactions Table */}
          <AccountingListTable
            entries={entries}
            totalCredit={totalCredit}
            totalDebit={totalDebit}
            receivable={receivable}
            payable={payable}
            fmt={fmt}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
}

type SummaryCardProps = {
  label: string;
  value: string;
  icon: ReactNode;
  tone: "green" | "red" | "blue" | "slate";
};

function SummaryCard({ label, value, icon, tone }: SummaryCardProps) {
  const toneClass = {
    green: "bg-[#ECFDF5] text-[#0E9F6E]",
    red: "bg-[#FEF2F2] text-[#D14343]",
    blue: "bg-[#F0F4FF] text-[#1A5EF9]",
    slate: "bg-[#F5F8FD] text-[#52627C]",
  }[tone];

  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-4 shadow-[0_8px_24px_rgba(26,94,249,0.06)]">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-[#52627C]">{label}</p>
        <div className={cn("rounded-full p-2", toneClass)}>{icon}</div>
      </div>
      <p className="mt-3 text-xl font-bold text-[#21314A]">OMR {value}</p>
    </div>
  );
}

export default AccountingReport;
